'use client';

import { useCallback, useState } from 'react';

import { setScratched } from '@/actions/setScratched';
import type {
  SetScratchedParams,
  SetScratchedRequest,
  SetScratchedResponse,
} from '@/types/models/SetScratched';
import { logger } from '@/utils';

type UseSetScratchedReturn = {
  data: SetScratchedResponse | undefined;
  loading: boolean;
  error: string | null;
  setTicketsAsScratched: (
    params: SetScratchedParams,
    payload: SetScratchedRequest,
  ) => Promise<SetScratchedResponse | undefined>;
  clearError: () => void;
  retry: () => void;
};

export const useSetScratched = (): UseSetScratchedReturn => {
  const [data, setData] = useState<SetScratchedResponse | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRequest, setLastRequest] = useState<{
    params: SetScratchedParams;
    payload: SetScratchedRequest;
  } | null>(null);

  const setTicketsAsScratched = useCallback(
    async (params: SetScratchedParams, payload: SetScratchedRequest, retryCount = 0) => {
      setLoading(true);
      setError(null);

      try {
        const response = await setScratched(params, payload);
        setData(response);
        setLastRequest({ params, payload });
        return response;
      } catch (err) {
        logger.error('Error setting tickets as scratched:', err);

        setData(undefined);

        if (
          err instanceof Error &&
          (err.message.includes('ECONNRESET') || err.message.includes('aborted')) &&
          retryCount < 2
        ) {
          await new Promise((resolve) => {
            setTimeout(resolve, 2000 * (retryCount + 1));
          });

          return await setTicketsAsScratched(params, payload, retryCount + 1);
        }

        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const retry = useCallback(() => {
    if (lastRequest) {
      setTicketsAsScratched(lastRequest.params, lastRequest.payload);
    }
  }, [lastRequest, setTicketsAsScratched]);

  return {
    data,
    loading,
    error,
    setTicketsAsScratched,
    clearError,
    retry,
  };
};
